import React, {useEffect} from 'react';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserIcon,
} from '@heroicons/react/outline';
import {useAppContext, useAnalytics} from '../../hooks';
import PlanCard from '../../components/PlanCard';
import DocumentHeader from '../../components/DocumentHeader';
import landingPageImage from '../../../assets/images/interview.jpg';
import {Grid, Row, Col} from '../../components/Grid';
import FAQs from '../../components/FAQs';
import Button, {ButtonSizes} from '../../components/ButtonNew';
import Testimonials from '../../components/Testimonials';
import {resumeReviewFeatures, resumeReviewCallFeatures} from '../../constants/product';

import Nick from '../../../assets/images/testimonials/nick.jpeg';
import Jake from '../../../assets/images/testimonials/jake.jpg';

const features = [
  {
    name: 'Expert senior tech reviewer',
    description: 'Get feedback from experienced hiring managers and recruiters who have reviewed thousands of applications at top tech companies. Learn what actually matters to hiring managers.',
    icon: UserIcon,
  },
  {
    name: 'Personalized resume feedback',
    description: 'Forget the generic advice. We help you craft the perfect resume with specific, actionable improvements based on your target role and experiences.',
    icon: DocumentTextIcon,
  },
  {
    name: 'Money-back guarantee',
    description: "We're not satisfied until you are. Work with us until you get the resume you want. If you're not satisfied, get your money back.",
    icon: CurrencyDollarIcon,
  },
];

const testimonials = [
  {
    name: 'Nick Culpin',
    title: 'Software Engineer',
    image: Nick.src,
    quote: 'This framework was exactly the kind of clarity I was looking for, in particular the job skills and experience section helped me to improve how I articulate data analysis and trim the fat on my resume.',
  },
  {
    name: 'Jake',
    title: 'Software Engineer',
    image: Jake.src,
    quote: 'Thanks for the feedback on my resume; this is the exact kind of candid shake-up feedback I was hoping to receive.',
  },
];

const ResumeFAQs = [
  {
    question: "What's included in my resume review?",
    answer: 'For the live 45-minute video call, your reviewer will walk you through specific recommendations and suggestions to improve your resume. If you opt for review over email instead, all this feedback will be sent to you in a personalized document. Our resume experts will explain how to edit the language, format, and details of your resume to maximize the chances of your application.',
  },
  {
    question: 'How soon will I receive my resume review?',
    answer: "Our team starts reviewing your resume right away. For the 45-minute video call, you'll receive feedback during your scheduled session. For email reviews, your review will be sent as soon as it's ready. We guarantee completion within 5 business days, but in most cases, you can expect to receive it within 48 hours or less.",
  },
  {
    question: "What's your refund policy?",
    answer: "We're confident that you'll find our resume review helpful and informative. If you aren't satisfied with your review for any reason, contact us for a re-review or get a full refund.",
  },
  {
    question: "Can I get my resume reviewed again if I've already submitted it once?",
    answer: 'Each resume review includes reviewing one version of your resume, but you are welcome to purchase a follow-up review for feedback on your updated resume.',
  },
  {
    question: 'Can Exponent review my cover letter or LinkedIn profile?',
    answer: "We don't offer these services yet, but if you're interested in these services, feel free to reach out to support@tryexponent.com.",
  },
];

export default function ResumeLandingPage() {
  const {tracker} = useAnalytics();
  const {catalog} = useAppContext();

  useEffect(() => {
    tracker.resumeViewLanding();
  }, []);

  return (
    <div className="relative bg-gray-50 overflow-hidden">
      <DocumentHeader
        title="Craft the Perfect Resume for More Interviews"
        socialDescription="Find out what your application is missing, stand out to recruiters, and land more interviews."
      />
      <div className="relative pt-6 pb-10 sm:pb-18">
        <main className="mt-16 mx-auto max-w-5xl px-4 sm:mt-24">
          <Grid>
            <Row className="justify-center">
              <Col md={6} sm={6} xs={12} className="max-w-lg">
                <h1 className="text-3xl tracking-tight font-extrabold text-gray-900 sm:text-4xl md:text-5xl">
                  Get more interviews with the perfect resume.
                </h1>
                <p className="mt-3 max-w-md text-base font-medium text-gray-500 sm:text-lg md:mt-5 md:text-xl">
                  Find out what your application is missing,
                  stand out to recruiters, and land more interviews.
                </p>
                <div className="my-6">
                  <Button
                    size={ButtonSizes.LARGE}
                    href="/resume/upload"
                    onClick={() => tracker.resumeClickUpload({button: 'top'})}
                  >
                    Upload resume to get started →
                  </Button>
                </div>
              </Col>
              <Col md={6} sm={6} xs={12} className="max-w-md">
                <img
                  src={landingPageImage.src}
                  className="pointer-events-none rounded-xl"
                  alt=""
                />
              </Col>
            </Row>
          </Grid>
        </main>
      </div>

      <div className="mt-10 mx-auto max-w-7xl px-4 pb-10 text-center">
        <h2 className="text-3xl tracking-tight font-extrabold text-gray-900">
          Features
        </h2>
      </div>

      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8 space-y-12 md:space-y-32">
        <dl className="space-y-10 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
          {features.map((feature) => (
            <div key={feature.name}>
              <dt>
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo text-white">
                  <feature.icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="mt-5 text-lg leading-6 font-medium text-gray-900">
                  {feature.name}
                </p>
              </dt>
              <dd className="mt-2 text-base text-gray-500">
                {feature.description}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <Testimonials testimonials={testimonials} />

      <div className="mt-10 mx-auto max-w-7xl px-4 pb-10 text-center">
        <h2 className="text-3xl tracking-tight font-extrabold text-gray-900">
          Pricing
        </h2>
      </div>

      <div className="flex flex-wrap gap-x-6 justify-center overflow-x-auto">
        <PlanCard
          name="Review over email"
          description="Get detailed resume feedback from an expert"
          features={resumeReviewFeatures}
          price={catalog?.RESUME_REVIEW.priceString}
          priceSecondaryText="per review"
          cta="Upload resume →"
          href="/resume/upload"
          onClick={() => tracker.resumeClickUpload({button: 'emailPlanCard'})}
          className="mb-4"
        />
        <PlanCard
          name="45-min call"
          description="Chat 1-on-1 with an expert for real-time resume feedback"
          badge={{
            text: 'Popular',
            backgroundColor: '#D8F7FE',
            textColor: '#2174A3',
          }}
          highlighted
          features={resumeReviewCallFeatures}
          price={catalog?.RESUME_REVIEW_CALL.priceString}
          priceSecondaryText="per session"
          cta="Upload resume →"
          href="/resume/upload?from=call"
          onClick={() => tracker.resumeClickUpload({button: 'callPlanCard'})}
          className="mb-4"
        />
      </div>

      <div className="mt-16 mx-auto max-w-7xl px-4 pb-8 text-center">
        <h2 className="text-3xl tracking-tight font-extrabold text-gray-900">
          FAQs
        </h2>
      </div>

      <FAQs items={ResumeFAQs} />
    </div>

  );
}
